import type {
  AgentTraceInspectionEntry,
  ConversationToolCall,
} from "./types.js";

export type ChildAgentEvidenceTimelineItem =
  | {
      kind: "message";
      id: string;
      sequence: number;
      occurredAt: string;
      text: string;
      phase: string | null;
      streaming: boolean;
    }
  | {
      kind: "reasoning";
      id: string;
      sequence: number;
      occurredAt: string;
      text: string;
      streaming: boolean;
    }
  | {
      kind: "tools";
      id: string;
      sequence: number;
      occurredAt: string;
      calls: ConversationToolCall[];
      active: boolean;
    }
  | {
      kind: "warning";
      id: string;
      sequence: number;
      occurredAt: string;
      title: string;
      detail: string;
    };

export interface ChildAgentEvidenceProjection {
  timeline: ChildAgentEvidenceTimelineItem[];
  technicalEntries: AgentTraceInspectionEntry[];
  hiddenReasoningEntries: number;
  todo: { current: string; completed: number; total: number } | null;
}

interface NativeEvent {
  method: string;
  params: Record<string, unknown>;
  item: Record<string, unknown> | null;
  itemId: string | null;
}

interface TextAccumulator {
  id: string;
  sequence: number;
  occurredAt: string;
  text: string;
  phase: string | null;
}

interface ToolAccumulator {
  id: string;
  runId: string;
  toolCallId: string;
  sequence: number;
  occurredAt: string;
  startedAt: string;
  finishedAt: string | null;
  tool: string;
  kind: ConversationToolCall["kind"];
  summary: string;
  input: string | null;
  output: string | null;
  status: ConversationToolCall["status"];
  errorClass: string | null;
  durationMs: number | null;
  item: Record<string, unknown> | null;
  outputDeltas: string[];
}

type AtomicTimelineItem = Exclude<
  ChildAgentEvidenceTimelineItem,
  { kind: "tools" }
> | {
  kind: "tool";
  id: string;
  sequence: number;
  occurredAt: string;
  call: ConversationToolCall;
};

/**
 * Turns the observable child lane back into the same meaningful units used by
 * the main conversation. Native token deltas and tool lifecycle pairs are
 * coalesced for readability; every retained entry remains available through
 * `technicalEntries` so this projection never becomes the only evidence view.
 */
export function projectChildAgentEvidence(
  entries: readonly AgentTraceInspectionEntry[],
): ChildAgentEvidenceProjection {
  const technicalEntries = [...entries].sort(compareEntries);
  const parsed = technicalEntries.map((entry) => ({
    entry,
    native: nativeEvent(entry),
  }));
  const latestPlan = parsed.filter(item => item.native?.method === "turn/plan/updated").at(-1)?.native?.params.plan;
  const steps = Array.isArray(latestPlan) ? latestPlan.map(record).filter((item): item is Record<string, unknown> =>
    item !== null && typeof item.step === "string" && ["pending", "inProgress", "in_progress", "completed"].includes(String(item.status))) : [];
  const current = steps.find(item => ["inProgress", "in_progress"].includes(String(item.status))) ?? steps.find(item => item.status === "pending");
  const todo = steps.length ? { current: String(current?.step ?? "Tasks complete"),
    completed: steps.filter(item => item.status === "completed").length, total: steps.length } : null;
  const completedMessageIds = new Set(
    parsed.flatMap(({ native }) =>
      native &&
      native.method === "item/completed" &&
      normalizedItemType(native.item) === "agentmessage" &&
      native.itemId
        ? [native.itemId]
        : [],
    ),
  );
  const atomic: AtomicTimelineItem[] = [];
  const messageDeltas = new Map<string, TextAccumulator>();
  const reasoningDeltas = new Map<string, TextAccumulator>();
  const tools = new Map<string, ToolAccumulator>();
  let hiddenReasoningEntries = 0;

  for (const { entry, native } of parsed) {
    if (entry.title === "Private model reasoning unavailable") {
      hiddenReasoningEntries += 1;
    }

    if (native) {
      if (
        native.method === "item/completed" &&
        normalizedItemType(native.item) === "agentmessage"
      ) {
        const content = stringValue(native.item?.text);
        if (content) {
          atomic.push({
            kind: "message",
            id: native.itemId ?? entry.id,
            sequence: entry.firstSequence,
            occurredAt: entry.occurredAt,
            text: content,
            phase: stringValue(native.item?.phase),
            streaming: false,
          });
        }
        continue;
      }

      if (native.method === "item/agentMessage/delta") {
        const delta = stringValue(native.params.delta);
        const id = native.itemId ?? entry.id;
        if (delta && !completedMessageIds.has(id)) {
          appendText(messageDeltas, id, entry, delta, null);
        }
        continue;
      }

      if (
        native.method === "item/reasoning/summaryTextDelta" ||
        native.method === "item/reasoning/summaryDelta"
      ) {
        const delta =
          stringValue(native.params.delta) ??
          stringValue(native.params.summary);
        if (delta) {
          appendText(
            reasoningDeltas,
            native.itemId ?? entry.id,
            entry,
            delta,
            null,
          );
        }
        continue;
      }

      if (updateNativeTool(tools, entry, native)) continue;

      // Routine engine diagnostics belong in Run evidence, not the task view.
      if (native.method === "warning") continue;
      if (native.method === "guardianWarning" || native.method === "error") {
        atomic.push({
          kind: "warning",
          id: entry.id,
          sequence: entry.firstSequence,
          occurredAt: entry.occurredAt,
          title:
            native.method === "guardianWarning"
              ? "Sub-agent safety warning"
              : "Sub-agent error",
          detail: nativeWarningDetail(native.params),
        });
        continue;
      }
    }

    if (entry.kind === "message" && entry.detailFormat === "text") {
      if (entry.detail) {
        atomic.push({
          kind: "message",
          id: entry.id,
          sequence: entry.firstSequence,
          occurredAt: entry.occurredAt,
          text: entry.detail,
          phase: entry.summary,
          streaming: entry.title !== "Assistant message",
        });
      }
      continue;
    }

    if (entry.kind === "reasoning" && entry.detailFormat === "text") {
      if (entry.detail) {
        atomic.push({
          kind: "reasoning",
          id: entry.id,
          sequence: entry.firstSequence,
          occurredAt: entry.occurredAt,
          text: entry.detail,
          streaming: entry.title !== "Reasoning summary",
        });
      }
      continue;
    }

    if (entry.kind === "tool") {
      updateRuntimeTool(tools, entry);
      continue;
    }

    if (entry.kind === "workspace") {
      updateWorkspaceTool(tools, entry);
    }
  }

  for (const message of messageDeltas.values()) {
    atomic.push({
      kind: "message",
      ...message,
      streaming: true,
    });
  }
  for (const reasoning of reasoningDeltas.values()) {
    atomic.push({
      kind: "reasoning",
      id: reasoning.id,
      sequence: reasoning.sequence,
      occurredAt: reasoning.occurredAt,
      text: reasoning.text,
      streaming: true,
    });
  }
  for (const tool of tools.values()) {
    const call = toolCall(tool);
    atomic.push({
      kind: "tool",
      id: call.id,
      sequence: call.sequence,
      occurredAt: call.startedAt,
      call,
    });
  }

  const readable = deduplicateStreamingMessages(atomic);
  readable.sort((left, right) =>
    left.sequence - right.sequence || left.id.localeCompare(right.id),
  );

  return {
    timeline: groupTools(readable),
    technicalEntries,
    hiddenReasoningEntries,
    todo,
  };
}

function deduplicateStreamingMessages(
  entries: readonly AtomicTimelineItem[],
): AtomicTimelineItem[] {
  const completedMessages = entries.filter(
    (
      entry,
    ): entry is Extract<AtomicTimelineItem, { kind: "message" }> =>
      entry.kind === "message" && !entry.streaming,
  );
  return entries.filter((entry) => {
    if (entry.kind !== "message" || !entry.streaming) return true;
    const content = entry.text.trim();
    return !completedMessages.some(
      (completed) =>
        completed.sequence >= entry.sequence && completed.text.trim() === content,
    );
  });
}

function appendText(
  values: Map<string, TextAccumulator>,
  id: string,
  entry: AgentTraceInspectionEntry,
  delta: string,
  phase: string | null,
): void {
  const current = values.get(id);
  if (current) {
    current.text += delta;
    current.phase ??= phase;
    return;
  }
  values.set(id, {
    id,
    sequence: entry.firstSequence,
    occurredAt: entry.occurredAt,
    text: delta,
    phase,
  });
}

function updateNativeTool(
  tools: Map<string, ToolAccumulator>,
  entry: AgentTraceInspectionEntry,
  native: NativeEvent,
): boolean {
  const itemType = normalizedItemType(native.item);
  const progressType = nativeProgressItemType(native.method);
  const tool = nativeToolName(itemType || progressType, native.item);
  if (!tool) return false;

  const id = entry.toolCallId ?? native.itemId ?? entry.id;
  const current =
    tools.get(id) ??
    newToolAccumulator({
      id,
      entry,
      tool,
      kind: itemType === "commandexecution" ? "command" : "tool",
    });
  current.sequence = Math.min(current.sequence, entry.firstSequence);
  if (entry.firstSequence <= current.sequence) {
    current.occurredAt = entry.occurredAt;
    current.startedAt = entry.occurredAt;
  }
  current.item = native.item ?? current.item;
  current.tool = tool;
  current.kind = itemType === "commandexecution" ? "command" : current.kind;

  if (native.method === "item/started") {
    current.status = "running";
    current.startedAt = entry.occurredAt;
  } else if (native.method === "item/completed") {
    current.status = nativeToolStatus(native.item, true);
    current.finishedAt = entry.occurredAt;
    current.durationMs =
      finiteNumber(native.item?.durationMs) ??
      elapsedDuration(current.startedAt, current.finishedAt);
    current.errorClass =
      current.status === "failed" ? "codex_tool_failed" : null;
  } else {
    const delta = stringValue(native.params.delta);
    if (delta) current.outputDeltas.push(delta);
    if (
      native.method === "item/fileChange/patchUpdated" &&
      Array.isArray(native.params.changes)
    ) {
      current.item = {
        ...(current.item ?? {}),
        type: "fileChange",
        changes: native.params.changes,
      };
    }
  }
  tools.set(id, current);
  return true;
}

function updateRuntimeTool(
  tools: Map<string, ToolAccumulator>,
  entry: AgentTraceInspectionEntry,
): void {
  const detail = jsonRecord(entry.detail);
  const invocation = record(detail?.invocation);
  const result = record(detail?.result);
  const id = entry.toolCallId ?? entry.id;
  const tool = entry.title.replace(/\s+(?:started|finished)$/i, "") || "Agent tool";
  const current =
    tools.get(id) ??
    newToolAccumulator({
      id,
      entry,
      tool,
      kind: stringValue(invocation?.kind) === "command" ? "command" : "tool",
    });
  current.sequence = Math.min(current.sequence, entry.firstSequence);
  current.tool = tool;
  current.kind =
    stringValue(invocation?.kind) === "command" ? "command" : current.kind;
  current.summary = stringValue(invocation?.summary) ?? current.summary;
  current.input = printableValue(invocation?.input) ?? current.input;
  current.output = printableValue(result?.output ?? result) ?? current.output;
  current.errorClass = stringValue(detail?.errorClass) ?? current.errorClass;

  if (/\bstarted$/i.test(entry.title)) {
    current.status = "running";
    current.startedAt = entry.occurredAt;
  } else {
    current.status = statusFromSummary(entry.summary);
    current.finishedAt = entry.occurredAt;
    current.durationMs = durationFromSummary(entry.summary);
  }
  tools.set(id, current);
}

function updateWorkspaceTool(
  tools: Map<string, ToolAccumulator>,
  entry: AgentTraceInspectionEntry,
): void {
  const id = entry.toolCallId ?? entry.id;
  const current =
    tools.get(id) ??
    newToolAccumulator({
      id,
      entry,
      tool: "codex.file_change",
      kind: "tool",
    });
  current.status = "succeeded";
  current.finishedAt = entry.occurredAt;
  current.summary = entry.summary ?? "Workspace change";
  current.output = joinOutput(current.output, entry.detail);
  tools.set(id, current);
}

function newToolAccumulator({
  id,
  entry,
  tool,
  kind,
}: {
  id: string;
  entry: AgentTraceInspectionEntry;
  tool: string;
  kind: ConversationToolCall["kind"];
}): ToolAccumulator {
  return {
    id,
    runId: entry.runId,
    toolCallId: id,
    sequence: entry.firstSequence,
    occurredAt: entry.occurredAt,
    startedAt: entry.occurredAt,
    finishedAt: null,
    tool,
    kind,
    summary: entry.title,
    input: null,
    output: null,
    status: "running",
    errorClass: null,
    durationMs: null,
    item: null,
    outputDeltas: [],
  };
}

function toolCall(value: ToolAccumulator): ConversationToolCall {
  const item = value.item;
  const type = normalizedItemType(item);
  const input = value.input ?? nativeToolInput(type, item);
  const nativeOutput = nativeToolOutput(type, item);
  const output = joinOutput(
    joinOutput(value.output, nativeOutput),
    value.outputDeltas.join(""),
  );
  const command = type === "commandexecution" ? stringValue(item?.command) : null;
  return {
    id: value.toolCallId,
    runId: value.runId,
    tool: value.tool,
    kind: value.kind,
    summary: command ?? value.summary,
    input: command ?? input,
    output,
    status: value.status,
    errorClass: value.errorClass,
    durationMs: value.durationMs,
    startedAt: value.startedAt,
    finishedAt: value.finishedAt,
    sequence: value.sequence,
  };
}

function nativeToolInput(
  type: string,
  item: Record<string, unknown> | null,
): string | null {
  if (!item) return null;
  if (type === "commandexecution") return stringValue(item.command);
  const omitted = new Set([
    "aggregatedOutput",
    "output",
    "result",
    "error",
    "status",
    "durationMs",
    "exitCode",
  ]);
  const input = Object.fromEntries(
    Object.entries(item).filter(([key]) => !omitted.has(key)),
  );
  return printableValue(input);
}

function nativeToolOutput(
  type: string,
  item: Record<string, unknown> | null,
): string | null {
  if (!item) return null;
  if (type === "commandexecution") {
    return stringValue(item.aggregatedOutput) ?? stringValue(item.output);
  }
  if (type === "filechange") return fileChangeOutput(item.changes);
  return printableValue(item.result ?? item.output ?? item.error);
}

function fileChangeOutput(value: unknown): string | null {
  if (!Array.isArray(value) || value.length === 0) return null;
  return value
    .map(record)
    .filter((change): change is Record<string, unknown> => change !== null)
    .map((change) => {
      const path = stringValue(change.path) ?? "App file";
      const kindRecord = record(change.kind);
      const kind =
        stringValue(kindRecord?.type) ?? stringValue(change.kind) ?? "changed";
      const diff = stringValue(change.diff);
      return `${kind} ${path}${diff ? `\n${diff}` : ""}`;
    })
    .join("\n\n");
}

function nativeEvent(entry: AgentTraceInspectionEntry): NativeEvent | null {
  if (entry.detailFormat !== "json") return null;
  const detail = jsonRecord(entry.detail);
  const event = record(detail?.event);
  const method = stringValue(event?.method) ?? stringValue(event?.type);
  if (!method) return null;
  const params = record(event?.params) ?? {};
  const item = record(params.item);
  return {
    method,
    params,
    item,
    itemId:
      stringValue(detail?.nativeItemId) ??
      stringValue(item?.id) ??
      stringValue(params.itemId),
  };
}

function normalizedItemType(item: Record<string, unknown> | null): string {
  return (stringValue(item?.type) ?? "")
    .replace(/[_-]+/g, "")
    .toLocaleLowerCase();
}

function nativeProgressItemType(method: string): string {
  if (method.startsWith("item/commandExecution/")) return "commandexecution";
  if (method.startsWith("item/fileChange/")) return "filechange";
  if (method.startsWith("item/mcpToolCall/")) return "mcptoolcall";
  return "";
}

function nativeToolName(
  type: string,
  item: Record<string, unknown> | null,
): string | null {
  if (type === "commandexecution") return "codex.command";
  if (type === "filechange") return "codex.file_change";
  if (type === "websearch") return "codex.web_search";
  if (type === "collabagenttoolcall") return "codex.collaboration";
  if (type === "dynamictoolcall") return "codex.dynamic";
  if (type === "imageview") return "codex.image_view";
  if (type === "imagegeneration") return "codex.image_generation";
  if (type === "sleep") return "codex.sleep";
  if (type === "mcptoolcall") {
    const server = stringValue(item?.server);
    const tool = stringValue(item?.tool);
    return server && tool ? `codex.mcp.${server}.${tool}` : "codex.mcp";
  }
  return null;
}

function nativeToolStatus(
  item: Record<string, unknown> | null,
  completed: boolean,
): ConversationToolCall["status"] {
  const status = (stringValue(item?.status) ?? "").toLocaleLowerCase();
  const exitCode = finiteNumber(item?.exitCode);
  if (status === "cancelled" || status === "canceled" || status === "interrupted") {
    return "cancelled";
  }
  if (
    status === "failed" ||
    status === "declined" ||
    (exitCode !== null && exitCode !== 0) ||
    item?.success === false
  ) {
    return "failed";
  }
  return completed ? "succeeded" : "running";
}

function statusFromSummary(
  summary: string | null,
): ConversationToolCall["status"] {
  const value = summary?.toLocaleLowerCase() ?? "";
  if (value.includes("cancel")) return "cancelled";
  if (value.includes("fail") || value.includes("error")) return "failed";
  if (value.includes("succeed") || value.includes("complete")) return "succeeded";
  return "succeeded";
}

function durationFromSummary(summary: string | null): number | null {
  const match = summary?.match(/(?:^|·)\s*([\d.]+)\s*ms\b/i);
  if (!match) return null;
  const value = Number(match[1]);
  return Number.isFinite(value) ? value : null;
}

function elapsedDuration(startedAt: string, finishedAt: string): number | null {
  const duration = Date.parse(finishedAt) - Date.parse(startedAt);
  return Number.isFinite(duration) && duration >= 0 ? duration : null;
}

function groupTools(
  entries: readonly AtomicTimelineItem[],
): ChildAgentEvidenceTimelineItem[] {
  const groups: ChildAgentEvidenceTimelineItem[] = [];
  for (const entry of entries) {
    const previous = groups.at(-1);
    if (entry.kind === "tool" && previous?.kind === "tools") {
      previous.calls.push(entry.call);
      previous.active ||= entry.call.status === "running";
    } else if (entry.kind === "tool") {
      groups.push({
        kind: "tools",
        id: `tools:${entry.id}`,
        sequence: entry.sequence,
        occurredAt: entry.occurredAt,
        calls: [entry.call],
        active: entry.call.status === "running",
      });
    } else {
      groups.push(entry);
    }
  }
  return groups;
}

function nativeWarningDetail(params: Record<string, unknown>): string {
  const error = record(params.error);
  return (
    stringValue(params.message) ??
    stringValue(error?.message) ??
    printableValue(params) ??
    "The specialist reported a warning."
  );
}

function joinOutput(left: string | null, right: string | null): string | null {
  if (!right) return left;
  if (!left) return right;
  if (left.includes(right)) return left;
  if (right.includes(left)) return right;
  return `${left}\n\n${right}`;
}

function printableValue(value: unknown): string | null {
  if (typeof value === "string") return value || null;
  if (value === undefined || value === null) return null;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function jsonRecord(value: string | null): Record<string, unknown> | null {
  if (!value) return null;
  try {
    return record(JSON.parse(value));
  } catch {
    return null;
  }
}

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function stringValue(value: unknown): string | null {
  return typeof value === "string" && value ? value : null;
}

function finiteNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function compareEntries(
  left: AgentTraceInspectionEntry,
  right: AgentTraceInspectionEntry,
): number {
  return (
    left.firstSequence - right.firstSequence ||
    left.lastSequence - right.lastSequence ||
    left.id.localeCompare(right.id)
  );
}
